"use client";

import Image from "next/image";
import Link from "next/link";
import { useScrollToSection } from "../hooks/useScrollToSection";

const SITE_MAP = [
  { id: "hero", label: "Home" },
  { id: "capabilities", label: "Capabilities" },
  { id: "sectors", label: "Sectors" },
  { id: "terrakio", label: "Terrak.io" },
  { id: "our-work", label: "Our work" },
  { id: "about", label: "About" },
  { id: "how-we-work", label: "How we work" },
  { id: "contact", label: "Contact" },
];

const CASE_STUDIES = [
  {
    href: "/case-studies/australia-environment-report",
    label: "Australia’s Environment Report",
  },
  {
    href: "/case-studies/vmap-vegetation-monitoring",
    label: "VMAP vegetation monitoring",
  },
  { href: "/case-studies/planr-platform", label: "Nature Repair Market" },
  {
    href: "/case-studies/ozhazard-bushfire-intelligence",
    label: "OzHazard bushfire intelligence",
  },
  { href: "/case-studies/nsw-water-compliance", label: "NSW water compliance" },
  {
    href: "/case-studies/act-environmental-assessment",
    label: "ACT environmental assessment",
  },
];

const ColumnTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="dc-mono mb-6 flex items-center gap-3 text-[12px] uppercase tracking-[.2em] text-mist-3">
    <span className="h-px w-5 bg-mint" />
    {children}
  </div>
);

const linkClass =
  "text-[15px] leading-[1.4] text-mist-2 transition-colors hover:text-mint";

export const Footer = () => {
  const scrollToSection = useScrollToSection();

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-ink-4 px-[clamp(16px,4vw,64px)]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(rgba(127,232,180,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(127,232,180,.05) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="dc-shell relative">
        <div className="grid gap-x-[clamp(32px,4vw,72px)] gap-y-14 py-[clamp(56px,7vw,100px)] md:grid-cols-2 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,.9fr)]">
          <div>
            <Image
              src="/images/logo.png"
              alt="Haizea Analytics"
              width={152}
              height={64}
              className="mb-7 h-11 w-auto object-contain"
              draggable={false}
            />
            <p className="mt-0 mb-6 max-w-[34ch] text-[16px] leading-[1.7] text-mist-2">
              Spatial data intelligence from Canberra, Australia — turning
              complex Earth data into answers you can act on.
            </p>
            <div className="dc-mono text-[13px] tracking-[.1em] text-mint/80">
              &gt; Earth Data, Lightning Fast_
            </div>
          </div>

          <nav aria-label="Site map">
            <ColumnTitle>Site map</ColumnTitle>
            <ul className="m-0 grid list-none grid-cols-2 gap-x-8 gap-y-3.5 p-0">
              {SITE_MAP.map((item) => (
                <li key={item.id}>
                  <Link
                    href={`/#${item.id}`}
                    onClick={(event) => {
                      event.preventDefault();
                      scrollToSection(item.id);
                    }}
                    className={linkClass}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Case studies">
            <ColumnTitle>Case studies</ColumnTitle>
            <ul className="m-0 grid list-none gap-3.5 p-0">
              {CASE_STUDIES.map((item) => (
                <li key={item.href}>
                  <Link href={item.href} className={linkClass}>
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  href="/case-studies"
                  className="dc-mono text-[12px] uppercase tracking-[.16em] text-mint transition-colors hover:text-paper"
                >
                  All case studies →
                </Link>
              </li>
            </ul>
          </nav>

          <div>
            <ColumnTitle>Contact</ColumnTitle>
            <address className="mb-5 text-[15px] not-italic leading-[1.8] text-mist-2">
              Gould Building (B116), Daley Road
              <br />
              Canberra ACT 2600, Australia
            </address>
            <div className="grid gap-2.5">
              <a
                href="mailto:contact@haizea.com.au"
                className="text-[15px] font-medium text-mint transition-colors hover:text-paper"
              >
                contact@haizea.com.au
              </a>
              <a href="https://terrak.io" className={linkClass}>
                terrak.io ↗
              </a>
            </div>
          </div>
        </div>

        <div className="dc-mono flex flex-wrap justify-between gap-x-6 gap-y-3 border-t border-white/[.08] py-7 text-[12px] uppercase tracking-[.12em] text-mist-4">
          <span>
            © {new Date().getFullYear()} Haizea Analytics. All rights reserved.
          </span>
          <span>Haizea Analytics Pty Ltd · ABN 27 648 451 969</span>
        </div>
      </div>
    </footer>
  );
};
